import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Typography, Stack, Chip, Alert,
  Select, MenuItem, Checkbox, FormControlLabel, Box, Collapse, IconButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { calendarApi } from '../api/services';
import type { CalendarEvent, AnnualStrategicDriver } from '../types/models';

interface CalendarEventDetailProps {
  event: CalendarEvent | null;
  open: boolean;
  onClose: () => void;
  onDriverLinked: () => void;
  drivers: AnnualStrategicDriver[];
}

function formatEventTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function getBorderColor(event: CalendarEvent): string {
  if (event.isPriorityMeeting) return 'error.main';
  if (event.isPrivate) return 'warning.main';
  return 'primary.main';
}

export default function CalendarEventDetail({
  event,
  open,
  onClose,
  onDriverLinked,
  drivers,
}: CalendarEventDetailProps) {
  const [selectedDriverId, setSelectedDriverId] = useState<string>('');
  const [applyToRecurring, setApplyToRecurring] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);

  // Reset state when event changes
  React.useEffect(() => {
    if (event) {
      setSelectedDriverId(event.strategicDriverId || '');
      setApplyToRecurring(false);
      setFeedback(null);
      setDescriptionExpanded(false);
    }
  }, [event]);

  const handleDriverChange = async (driverId: string) => {
    if (!event) return;
    setSelectedDriverId(driverId);
    setSaving(true);
    setFeedback(null);
    try {
      await calendarApi.linkToDriver(event.id, {
        strategicDriverId: driverId || null,
        applyToRecurring,
      });
      setFeedback({ type: 'success', text: 'Strategic driver updated successfully' });
      onDriverLinked();
    } catch (err: any) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Failed to link driver';
      setFeedback({
        type: 'error',
        text: typeof msg === 'string' ? msg : 'Failed to link driver',
      });
    } finally {
      setSaving(false);
    }
  };

  if (!event) return null;

  const currentDriver = drivers.find((d) => d.id === event.strategicDriverId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          borderLeft: 4,
          borderColor: getBorderColor(event),
          ml: 0,
        }}
      >
        {event.title}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {/* Time */}
          <Stack direction="row" spacing={1} alignItems="center">
            <ScheduleIcon fontSize="small" color="action" />
            <Typography variant="body2">
              {event.isAllDay
                ? 'All day'
                : `${formatEventTime(event.startTime)} - ${formatEventTime(event.endTime)}`}
            </Typography>
          </Stack>

          {/* Location */}
          {event.location && (
            <Stack direction="row" spacing={1} alignItems="center">
              <LocationIcon fontSize="small" color="action" />
              <Typography variant="body2">{event.location}</Typography>
            </Stack>
          )}

          {/* Attendees */}
          {event.attendees && (
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <PeopleIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {event.attendees}
              </Typography>
            </Stack>
          )}

          {/* Description (collapsible) */}
          {event.description && (
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                sx={{ cursor: 'pointer', userSelect: 'none' }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Description
                </Typography>
                <IconButton size="small">
                  {descriptionExpanded ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )}
                </IconButton>
              </Stack>
              <Collapse in={descriptionExpanded}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1, whiteSpace: 'pre-line' }}
                >
                  {event.description}
                </Typography>
              </Collapse>
            </Box>
          )}

          {/* Strategic Driver section */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Strategic Driver
            </Typography>

            {currentDriver ? (
              <Chip
                label={currentDriver.title}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ mb: 1.5 }}
              />
            ) : (
              <Chip
                label="No driver linked"
                size="small"
                variant="outlined"
                sx={{ mb: 1.5, color: 'text.secondary' }}
              />
            )}

            <Select
              value={selectedDriverId}
              onChange={(e) => handleDriverChange(e.target.value)}
              fullWidth
              size="small"
              displayEmpty
              disabled={saving}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {drivers.map((driver) => (
                <MenuItem key={driver.id} value={driver.id}>
                  {driver.title}
                </MenuItem>
              ))}
            </Select>

            <FormControlLabel
              control={
                <Checkbox
                  checked={applyToRecurring}
                  onChange={(e) => setApplyToRecurring(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  Apply to all recurring instances
                </Typography>
              }
              sx={{ mt: 1 }}
            />
          </Box>

          {/* Feedback */}
          {feedback && (
            <Alert
              severity={feedback.type}
              onClose={() => setFeedback(null)}
            >
              {feedback.text}
            </Alert>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
